import { buildAccountStatusMessage } from './accountStatus'
import type { SyncFailureState } from './syncEngine'
import { syncAccountStateFromLegacy } from '../state/accountStore'
import {
   syncCurrentFastStateFromLegacy,
   syncFastHistoryFromLegacy,
   syncHistoryRemoteStateFromLegacy,
} from '../state/currentFastStore'
import { applyHabitsFromLegacy } from '../state/habitsStore'
import { measurementEntries } from '../state/measurementsStore'
import { applyRunEffortTargetFromLegacy } from '../state/runEffortTargetStore'
import { closeShareOverlay, shareOverlayState } from '../state/shareOverlayStore'
import {
   syncWeightStateFromLegacy,
   weightChartMode,
   type WeightEntry,
} from '../state/weightStore'
import type { FastDisplayMode, FastEntry } from '../utils/fastEntry'
import { parseTimestampToNumber } from '../utils/time'
import { formatQuantityInput, type UnitSystem } from '../utils/units'

type BridgeProfile = {
   heightCm: number | null
   targetWeightKg: number | null
   targetMonthlyRunEffort: number | null
   unitSystem: UnitSystem
}

type SolidStateBridgeDeps = {
   getCurrentFast: () => FastEntry | null
   getFastHistory: () => FastEntry[]
   getWeightHistory: () => WeightEntry[]
   getHabits: () => unknown[]
   getUserProfile: () => BridgeProfile
   getUserId: () => string
   canUseRemoteSync: () => boolean
   getHistoryRemoteState: () => { error: unknown; loadingPromise: Promise<void> | null }
   getDisplayModePreference: () => FastDisplayMode
   shouldShowStartupSyncLoading: () => boolean
   getShowWeightTarget: () => boolean
   getRemoteLoadInProgress: () => boolean
   getSyncServerUpdatedAt: () => unknown
   getSyncFailureState: () => SyncFailureState
}

type AccountOverrides = {
   statusMessage?: string
   downloadStatusMessage?: string
   isDownloading?: boolean
}

/**
 * Returns whether any account data exists locally.
 *
 * @param deps - The bridge dependencies.
 * @returns True when there is data to export or delete.
 */
function hasAnyAccountData(deps: SolidStateBridgeDeps) {
   const profile = deps.getUserProfile()
   return (
      Boolean(deps.getCurrentFast()) ||
      deps.getFastHistory().length > 0 ||
      deps.getWeightHistory().length > 0 ||
      deps.getHabits().length > 0 ||
      measurementEntries().some(entry => entry.deletedAt === null) ||
      typeof profile.heightCm === 'number' ||
      typeof profile.targetWeightKg === 'number' ||
      typeof profile.targetMonthlyRunEffort === 'number'
   )
}

/**
 * Recomputes and pushes account status, profile inputs, and key actions into the Solid store.
 *
 * @param deps - The bridge dependencies.
 * @param overrides - Optional status/download message overrides.
 */
function syncAccountStateToSolid(
   deps: SolidStateBridgeDeps,
   overrides: AccountOverrides = {},
) {
   const userId = deps.getUserId()
   const profile = deps.getUserProfile()
   const hasSyncKey = Boolean(userId)
   const serverTimestampMillis = parseTimestampToNumber(deps.getSyncServerUpdatedAt())
   const canGenerateQr = hasSyncKey && Number.isFinite(serverTimestampMillis)
   if (!canGenerateQr && shareOverlayState().source === 'account') {
      closeShareOverlay()
   }
   const hasDataToDelete = hasSyncKey || hasAnyAccountData(deps)
   applyRunEffortTargetFromLegacy(profile.targetMonthlyRunEffort)

   const statusMessage = buildAccountStatusMessage({
      hasSyncKey,
      isRemoteRefreshInFlight: hasSyncKey && deps.getRemoteLoadInProgress(),
      syncFailure: deps.getSyncFailureState(),
      heightCm: profile.heightCm,
      targetWeightKg: profile.targetWeightKg,
      targetMonthlyRunEffort: profile.targetMonthlyRunEffort,
      unitSystem: profile.unitSystem,
   })
   let downloadStatusMessage: string | undefined
   if (typeof overrides.downloadStatusMessage === 'string') {
      downloadStatusMessage = overrides.downloadStatusMessage
   } else if (!hasDataToDelete) {
      downloadStatusMessage = ''
   }
   syncAccountStateFromLegacy({
      heightInput: formatQuantityInput('length', profile.heightCm, profile.unitSystem),
      targetWeightInput: formatQuantityInput(
         'mass',
         profile.targetWeightKg,
         profile.unitSystem,
      ),
      targetRunEffortInput:
         typeof profile.targetMonthlyRunEffort === 'number' ?
            String(profile.targetMonthlyRunEffort)
         :  '',
      statusMessage:
         typeof overrides.statusMessage === 'string' ?
            overrides.statusMessage
         :  statusMessage,
      userId: hasSyncKey ? userId : '',
      showKeyActions: !hasSyncKey,
      showCreateQr: canGenerateQr,
      hasDataToDelete,
      downloadStatusMessage,
   })
}

/**
 * Creates the bridge that pushes legacy runtime state into the Solid stores.
 *
 * @param deps - Accessors over the legacy runtime's mutable state.
 * @returns The per-slice sync functions.
 */
export function createSolidStateBridge(deps: SolidStateBridgeDeps) {
   /**
    * Pushes remote-history loading/error status into the Solid store.
    *
    * @param nextState - Optional loading flag and error-message override.
    */
   function syncHistoryRemoteStateToSolid(
      nextState: { loading?: boolean; errorMessage?: string } = {},
   ) {
      let errorMessage = ''
      if (typeof nextState.errorMessage === 'string') {
         errorMessage = nextState.errorMessage
      } else if (deps.getHistoryRemoteState().error) {
         errorMessage = 'Unable to load remote history. Showing saved entries.'
      }
      syncHistoryRemoteStateFromLegacy({
         loading: nextState.loading === true,
         hasRemoteSync: Boolean(deps.getUserId() && deps.canUseRemoteSync()),
         errorMessage,
      })
   }

   /**
    * Pushes fast history and its remote status into the Solid store.
    */
   function syncHistoryStateToSolid() {
      syncFastHistoryFromLegacy(deps.getFastHistory())
      syncHistoryRemoteStateToSolid({
         loading: Boolean(deps.getHistoryRemoteState().loadingPromise),
      })
   }

   /**
    * Pushes the current fast and display mode into the Solid store.
    */
   function syncCurrentFastStateToSolid() {
      syncCurrentFastStateFromLegacy({
         currentFast: deps.getCurrentFast(),
         displayMode: deps.getDisplayModePreference(),
         startupSyncLoading: deps.shouldShowStartupSyncLoading(),
      })
   }

   /**
    * Pushes weight history, profile, and chart settings into the Solid store.
    */
   function syncWeightStateToSolid() {
      const profile = deps.getUserProfile()
      syncWeightStateFromLegacy({
         weightHistory: deps.getWeightHistory(),
         userProfile: {
            heightCm: profile.heightCm,
            targetWeightKg: profile.targetWeightKg,
         },
         showTarget: deps.getShowWeightTarget(),
         chartMode: weightChartMode(),
      })
   }

   return {
      syncAccountStateToSolid: (overrides: AccountOverrides = {}) =>
         syncAccountStateToSolid(deps, overrides),
      syncCurrentFastStateToSolid,
      syncHabitsStateToSolid: () => applyHabitsFromLegacy(deps.getHabits()),
      syncHistoryRemoteStateToSolid,
      syncHistoryStateToSolid,
      syncWeightStateToSolid,
   }
}
